<template>
  <div class="register-page py-3">
    <header class="register-toolbar">
      <div class="register-toolbar__title">
        <span class="register-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Create New User</h1>
          <p>Add a new team member to your organisation</p>
        </div>
      </div>
      <div class="register-toolbar__actions">
        <CButton size="sm" class="back-btn" @click="$router.push(usersListPath)">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" class="back-btn__icon" aria-hidden="true">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          <span>Back to Users</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="register-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="register-alert">{{ successMessage }}</CAlert>

    <form class="register-form" @submit.prevent="register">
      <!-- Profile section -->
      <section class="register-section">
        <header class="register-section__head">
          <div class="register-section__heading">
            <span class="register-section__step">1</span>
            <div>
              <h2>Profile</h2>
              <p>Photo and contact details for the new user</p>
            </div>
          </div>
        </header>

        <div class="register-section__body">
          <div class="profile-row">
            <div class="profile-photo">
              <div class="profile-photo__avatar" :style="!photoPreview ? { background: avatarGradient } : {}">
                <img v-if="photoPreview" :src="photoPreview" alt="Profile preview" />
                <span v-else>{{ photoInitials }}</span>
              </div>
              <input ref="photoInput" type="file" accept="image/*" class="profile-photo__input"
                @change="onPhotoSelected" />
              <div class="profile-photo__actions">
                <button type="button" class="ghost-btn" @click="triggerPhotoPicker">
                  {{ photoPreview ? 'Change Photo' : 'Upload Photo' }}
                </button>
                <button v-if="photoPreview" type="button" class="ghost-btn ghost-btn--danger" @click="clearPhoto">
                  Remove
                </button>
              </div>
              <p class="profile-photo__hint">PNG or JPG, up to 2 MB.</p>
            </div>

            <div class="profile-fields">
              <div class="form-grid">
                <div class="form-field">
                  <label class="form-label" for="reg-name">Full name</label>
                  <input id="reg-name" v-model.trim="form.name" type="text" class="form-input" autocomplete="name"
                    placeholder="e.g. Anitha Raj" />
                </div>

                <div class="form-field">
                  <label class="form-label" for="reg-email">Email</label>
                  <input id="reg-email" v-model.trim="form.email" type="email" class="form-input" autocomplete="email"
                    placeholder="name@example.com" />
                </div>

                <div class="form-field">
                  <label class="form-label" for="reg-phone">Phone number</label>
                  <input id="reg-phone" v-model.trim="form.phone" type="tel" class="form-input" autocomplete="tel"
                    placeholder="+91 98xxxxxxxx" />
                </div>

                <div class="form-field">
                  <label class="form-label" for="reg-alt-phone">Alternate phone</label>
                  <input id="reg-alt-phone" v-model.trim="form.alternate_phone" type="tel" class="form-input"
                    placeholder="Optional" />
                </div>

                <div class="form-field">
                  <label class="form-label">Date of birth</label>
                  <SingleDatePicker v-model="form.date_of_birth" />
                </div>

                <div class="form-field">
                  <label class="form-label">Gender</label>
                  <AppDropdown v-model="form.gender" :options="genderOptions" placeholder="Select gender" />
                </div>

                <div class="form-field">
                  <label class="form-label">Blood group</label>
                  <AppDropdown v-model="form.blood_group" :options="bloodGroupOptions" placeholder="Select" />
                </div>

                <div class="form-field">
                  <label class="form-label">Marital status</label>
                  <AppDropdown v-model="form.marital_status" :options="maritalOptions" placeholder="Select" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Address section -->
      <section class="register-section">
        <header class="register-section__head">
          <div class="register-section__heading">
            <span class="register-section__step">2</span>
            <div>
              <h2>Address</h2>
              <p>Where this user is based</p>
            </div>
          </div>
          <span class="register-hint-pill">Optional</span>
        </header>

        <div class="register-section__body">
          <div class="form-grid form-grid--single">
            <div class="form-field">
              <label class="form-label" for="reg-address">Street address</label>
              <textarea id="reg-address" v-model="form.address" rows="3" class="form-input form-textarea"
                placeholder="House / building, street, locality"></textarea>
            </div>

            <div class="form-grid form-grid--quad">
              <div class="form-field">
                <label class="form-label" for="reg-city">City</label>
                <input id="reg-city" v-model.trim="form.city" type="text" class="form-input"
                  autocomplete="address-level2" placeholder="City" />
              </div>
              <div class="form-field">
                <label class="form-label" for="reg-state">State</label>
                <input id="reg-state" v-model.trim="form.state" type="text" class="form-input"
                  autocomplete="address-level1" placeholder="State" />
              </div>
              <div class="form-field">
                <label class="form-label" for="reg-pincode">Pincode</label>
                <input id="reg-pincode" v-model.trim="form.pincode" type="text" inputmode="numeric" pattern="[0-9]*"
                  maxlength="10" class="form-input" autocomplete="postal-code" placeholder="6-digit" />
              </div>
              <div class="form-field">
                <label class="form-label" for="reg-country">Country</label>
                <input id="reg-country" v-model.trim="form.country" type="text" class="form-input"
                  autocomplete="country-name" placeholder="Country" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Emergency contact -->
      <section class="register-section">
        <header class="register-section__head">
          <div class="register-section__heading">
            <span class="register-section__step">3</span>
            <div>
              <h2>Emergency Contact</h2>
              <p>Whom to reach in case of an emergency</p>
            </div>
          </div>
          <span class="register-hint-pill">Optional</span>
        </header>

        <div class="register-section__body">
          <div class="form-grid form-grid--triple">
            <div class="form-field">
              <label class="form-label" for="reg-ec-name">Contact name</label>
              <input id="reg-ec-name" v-model.trim="form.emergency_contact_name" type="text" class="form-input"
                placeholder="Full name" />
            </div>
            <div class="form-field">
              <label class="form-label" for="reg-ec-phone">Contact phone</label>
              <input id="reg-ec-phone" v-model.trim="form.emergency_contact_phone" type="tel" class="form-input"
                placeholder="Phone number" />
            </div>
            <div class="form-field">
              <label class="form-label">Relation</label>
              <AppDropdown v-model="form.emergency_contact_relation" :options="relationOptions"
                placeholder="Select relation" searchable />
            </div>
          </div>
        </div>
      </section>

      <!-- Access section -->
      <section class="register-section">
        <header class="register-section__head">
          <div class="register-section__heading">
            <span class="register-section__step">4</span>
            <div>
              <h2>Access</h2>
              <p>Where they belong and what they can do</p>
            </div>
          </div>
        </header>

        <div class="register-section__body">
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label">Organisation</label>
              <AppDropdown v-model="form.organisation_id" :options="organisationDropdownOptions" searchable
                search-placeholder="Search organisation…"
                :placeholder="isLoadingOrganisations ? 'Loading organisations…' : 'Select organisation'" />
            </div>
            <div class="form-field">
              <label class="form-label">Department</label>
              <AppDropdown v-model="form.department_id" :options="departmentDropdownOptions" searchable
                search-placeholder="Search department…"
                :placeholder="departmentPlaceholder" />
            </div>
            <div class="form-field">
              <label class="form-label">State</label>
              <AppDropdown v-model="form.state_id" :options="stateDropdownOptions" searchable
                search-placeholder="Search state…"
                :placeholder="isLoadingStates ? 'Loading states…' : 'Select state'" />
            </div>
            <div class="form-field">
              <label class="form-label">Branch</label>
              <AppDropdown v-model="form.branch_id" :options="branchDropdownOptions" searchable
                search-placeholder="Search branch…"
                :placeholder="branchPlaceholder" />
            </div>
            <div class="form-field form-field--span2">
              <label class="form-label">Role</label>
              <AppDropdown v-model="form.role" :options="roleDropdownOptions" searchable
                search-placeholder="Search role…" placeholder="Select a role" />
            </div>
          </div>
        </div>
      </section>

      <!-- Employment section -->
      <section class="register-section">
        <header class="register-section__head">
          <div class="register-section__heading">
            <span class="register-section__step">5</span>
            <div>
              <h2>Employment</h2>
              <p>HR details for the staff record</p>
            </div>
          </div>
          <span class="register-hint-pill">Optional</span>
        </header>

        <div class="register-section__body">
          <div class="form-grid form-grid--triple">
            <div class="form-field">
              <label class="form-label" for="reg-emp-code">Employee code</label>
              <input id="reg-emp-code" v-model.trim="form.employee_code" type="text" class="form-input"
                placeholder="e.g. MD-0042" />
            </div>
            <div class="form-field">
              <label class="form-label" for="reg-biometric">Biometric ID</label>
              <input id="reg-biometric" v-model.trim="form.biometric_id" type="text" class="form-input"
                placeholder="Fingerprint PIN on the device, e.g. 15" />
            </div>
            <div class="form-field">
              <label class="form-label" for="reg-designation">Designation</label>
              <input id="reg-designation" v-model.trim="form.designation" type="text" class="form-input"
                placeholder="e.g. Telesales Executive" />
            </div>
            <div class="form-field">
              <label class="form-label" for="reg-doj">Date of joining</label>
              <input id="reg-doj" v-model="form.date_of_joining" type="date" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label">Employment type</label>
              <AppDropdown v-model="form.employment_type" :options="employmentTypeOptions"
                placeholder="Select type" />
            </div>
            <div class="form-field">
              <label class="form-label">Employment status</label>
              <AppDropdown v-model="form.employment_status" :options="employmentStatusOptions"
                placeholder="Select status" />
            </div>
          </div>
        </div>
      </section>

      <!-- KYC & Bank section -->
      <section class="register-section">
        <header class="register-section__head">
          <div class="register-section__heading">
            <span class="register-section__step">6</span>
            <div>
              <h2>KYC &amp; Bank</h2>
              <p>Identity proof and payroll account details</p>
            </div>
          </div>
          <span class="register-hint-pill">Optional · Sensitive</span>
        </header>

        <div class="register-section__body">
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label" for="reg-pan">PAN number</label>
              <input id="reg-pan" v-model.trim="form.pan_number" type="text" class="form-input"
                maxlength="10" style="text-transform: uppercase" placeholder="ABCDE1234F" />
            </div>
            <div class="form-field">
              <label class="form-label" for="reg-aadhaar">Aadhaar number</label>
              <input id="reg-aadhaar" v-model.trim="form.aadhaar_number" type="text" inputmode="numeric"
                pattern="[0-9 ]*" maxlength="14" class="form-input" placeholder="12-digit" />
            </div>
            <div class="form-field">
              <label class="form-label" for="reg-bank-acc">Bank account number</label>
              <input id="reg-bank-acc" v-model.trim="form.bank_account_number" type="text" class="form-input"
                placeholder="Account number" />
            </div>
            <div class="form-field">
              <label class="form-label" for="reg-bank-name">Bank name</label>
              <input id="reg-bank-name" v-model.trim="form.bank_name" type="text" class="form-input"
                placeholder="e.g. State Bank of India" />
            </div>
            <div class="form-field">
              <label class="form-label" for="reg-bank-ifsc">IFSC</label>
              <input id="reg-bank-ifsc" v-model.trim="form.bank_ifsc" type="text" class="form-input"
                maxlength="11" style="text-transform: uppercase" placeholder="SBIN0001234" />
            </div>
          </div>
        </div>
      </section>

      <!-- CV / Document -->
      <section class="register-section">
        <header class="register-section__head">
          <div class="register-section__heading">
            <span class="register-section__step">7</span>
            <div>
              <h2>CV / Document</h2>
              <p>Upload the staff member's CV — PDF or image</p>
            </div>
          </div>
          <span class="register-hint-pill">Optional</span>
        </header>

        <div class="register-section__body">
          <div class="cv-upload">
            <input ref="cvInput" type="file" accept=".pdf,image/*" class="cv-upload__input" @change="onCvSelected" />
            <div class="cv-upload__row">
              <div class="cv-upload__info">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <span v-if="cvName" class="cv-upload__name">{{ cvName }}</span>
                <span v-else class="cv-upload__empty">No file chosen</span>
              </div>
              <div class="cv-upload__actions">
                <button type="button" class="ghost-btn" @click="triggerCvPicker">
                  {{ cvName ? 'Change File' : 'Upload File' }}
                </button>
                <button v-if="cvName" type="button" class="ghost-btn ghost-btn--danger" @click="clearCv">
                  Remove
                </button>
              </div>
            </div>
            <p class="cv-upload__hint">PDF, JPG, PNG or WEBP · up to 5 MB.</p>
          </div>
        </div>
      </section>

      <!-- Admin notes -->
      <section class="register-section">
        <header class="register-section__head">
          <div class="register-section__heading">
            <span class="register-section__step">8</span>
            <div>
              <h2>Admin Notes</h2>
              <p>Internal notes about this staff member</p>
            </div>
          </div>
          <span class="register-hint-pill">Optional</span>
        </header>

        <div class="register-section__body">
          <div class="form-grid form-grid--single">
            <div class="form-field">
              <textarea v-model="form.notes" rows="3" class="form-input form-textarea"
                placeholder="Any additional notes visible to admins"></textarea>
            </div>
          </div>
        </div>
      </section>

      <!-- Credentials section -->
      <section class="register-section">
        <header class="register-section__head">
          <div class="register-section__heading">
            <span class="register-section__step">9</span>
            <div>
              <h2>Credentials</h2>
              <p>Set the initial password — share it securely with the user</p>
            </div>
          </div>
        </header>

        <div class="register-section__body">
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label" for="reg-password">Password</label>
              <div class="password-wrap">
                <input id="reg-password" v-model="form.password" :type="showPassword ? 'text' : 'password'"
                  class="form-input" autocomplete="new-password" placeholder="At least 8 characters" />
                <button type="button" class="password-toggle"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'" @click="showPassword = !showPassword">
                  {{ showPassword ? 'Hide' : 'Show' }}
                </button>
              </div>
              <div v-if="form.password" class="password-strength" :class="`password-strength--${passwordStrength.key}`">
                <span class="password-strength__bar"><span
                    :style="{ width: passwordStrength.percent + '%' }"></span></span>
                <span class="password-strength__label">{{ passwordStrength.label }}</span>
              </div>
            </div>

            <div class="form-field">
              <label class="form-label" for="reg-password-confirm">Repeat password</label>
              <input id="reg-password-confirm" v-model="form.password_confirmation"
                :type="showPassword ? 'text' : 'password'" class="form-input"
                :class="{ 'form-input--error': passwordMismatch }" autocomplete="new-password"
                placeholder="Re-enter password" />
              <span v-if="passwordMismatch" class="form-error">Passwords do not match</span>
            </div>
          </div>
        </div>
      </section>

      <footer class="register-form__footer">
        <span class="form-meta">Required: name, email, password, organisation, role.</span>
        <div class="register-form__actions">
          <CButton type="button" size="sm" class="back-btn" :disabled="isSubmitting" @click="resetForm">
            Reset
          </CButton>
          <CButton type="submit" color="primary" size="sm" class="submit-btn"
            :disabled="isSubmitting || passwordMismatch">
            <svg v-if="!isSubmitting" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
              stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="submit-btn__icon"
              aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>{{ isSubmitting ? 'Creating…' : 'Create User' }}</span>
          </CButton>
        </div>
      </footer>
    </form>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import SingleDatePicker from '@/components/SingleDatePicker.vue'
import statesApi from '@/api/states.js'
import branchesApi from '@/api/branches.js'
import { ROLE_OPTIONS } from '@/views/users/userHelpers'
import { getStoredRoles } from '@/auth/access'

const PHOTO_MAX_BYTES = 2 * 1024 * 1024
const CV_MAX_BYTES = 5 * 1024 * 1024
const NO_DEPARTMENT = ''

const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say']
const MARITAL_OPTIONS = ['Single', 'Married', 'Divorced', 'Widowed', 'Separated']
const BLOOD_GROUP_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']
const EMPLOYMENT_TYPE_OPTIONS = ['full_time', 'part_time', 'contract', 'intern', 'consultant']
const EMPLOYMENT_STATUS_OPTIONS = ['active', 'on_leave', 'probation', 'suspended', 'resigned', 'terminated']
const RELATION_OPTIONS = ['Father', 'Mother', 'Spouse', 'Sibling', 'Child', 'Guardian', 'Friend', 'Other']

function emptyForm() {
  return {
    name: '',
    email: '',
    phone: '',
    alternate_phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    date_of_birth: '',
    gender: '',
    blood_group: '',
    marital_status: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
    employee_code: '',
    biometric_id: '',
    designation: '',
    date_of_joining: '',
    employment_type: '',
    employment_status: 'active',
    pan_number: '',
    aadhaar_number: '',
    bank_account_number: '',
    bank_name: '',
    bank_ifsc: '',
    notes: '',
    password: '',
    password_confirmation: '',
    organisation_id: null,
    department_id: NO_DEPARTMENT,
    state_id: null,
    branch_id: null,
    role: ''
  }
}

export default {
  name: 'Register',
  components: { AppDropdown, SingleDatePicker },
  data() {
    return {
      isSubmitting: false,
      errorMessage: '',
      successMessage: '',
      showPassword: false,
      photoPreview: '',
      photoFile: null,
      cvFile: null,
      cvName: '',
      organisations: [],
      departments: [],
      states: [],
      branches: [],
      isLoadingOrganisations: false,
      isLoadingDepartments: false,
      isLoadingStates: false,
      isLoadingBranches: false,
      genderOptions: GENDER_OPTIONS.map((v) => ({ value: v, label: v })),
      maritalOptions: MARITAL_OPTIONS.map((v) => ({ value: v, label: v })),
      bloodGroupOptions: BLOOD_GROUP_OPTIONS.map((v) => ({ value: v, label: v })),
      employmentTypeOptions: EMPLOYMENT_TYPE_OPTIONS.map((v) => ({ value: v, label: v.replace(/_/g, ' ') })),
      employmentStatusOptions: EMPLOYMENT_STATUS_OPTIONS.map((v) => ({ value: v, label: v.replace(/_/g, ' ') })),
      relationOptions: RELATION_OPTIONS.map((v) => ({ value: v, label: v })),
      form: emptyForm()
    }
  },
  computed: {
    organisationDropdownOptions() {
      return this.organisations.map((org) => ({ value: org.id, label: org.name }))
    },
    departmentDropdownOptions() {
      const options = this.departments.map((dept) => ({ value: dept.id, label: dept.name }))
      return [{ value: NO_DEPARTMENT, label: 'No department' }, ...options]
    },
    departmentPlaceholder() {
      if (!this.form.organisation_id) return 'Pick an organisation first'
      if (this.isLoadingDepartments) return 'Loading departments…'
      return 'Select department'
    },
    stateDropdownOptions() {
      return [
        { value: null, label: 'No state' },
        ...this.states.map((s) => ({ value: s.id, label: s.name }))
      ]
    },
    branchDropdownOptions() {
      // Cascade off State (both are global). With no state picked, all
      // branches show — picking a branch then auto-fills its state.
      const filtered = this.form.state_id
        ? this.branches.filter((b) => Number(b.state_id) === Number(this.form.state_id))
        : this.branches
      return [
        { value: null, label: 'No branch' },
        ...filtered.map((b) => ({ value: b.id, label: b.name }))
      ]
    },
    branchPlaceholder() {
      if (this.isLoadingBranches) return 'Loading branches…'
      if (!this.branches.length) return 'No branches in the system'
      if (this.form.state_id && !this.branchDropdownOptions.find((o) => o.value !== null)) return 'No branches under this state'
      return 'Select branch'
    },
    selectedDepartmentName() {
      const id = this.form.department_id
      if (!id) return ''
      const found = this.departments.find((dept) => dept.id === id)
      return found ? found.name : ''
    },
    // hr_manager (and only hr_manager — admins/superadmins are unrestricted) may
    // create tele sales staff and nothing else. The backend enforces the same
    // rule in AuthController::register; this just keeps the UI honest.
    isTelesalesOnlyCreator() {
      const roles = getStoredRoles().map((r) => String(r).toLowerCase())
      if (roles.some((r) => ['admin', 'superadmin'].includes(r))) return false
      return roles.includes('hr_manager')
    },
    // hr_manager reaches user creation via the scoped /staff shell (it is blocked
    // from /settings), so send it back there instead of /settings/users.
    usersListPath() {
      return this.isTelesalesOnlyCreator ? '/staff/users' : '/settings/users'
    },
    roleDropdownOptions() {
      if (this.isTelesalesOnlyCreator) {
        return [{ value: 'telesales', label: 'tele sales' }]
      }
      return ROLE_OPTIONS
        .filter((role) => role !== 'None')
        .map((role) => ({ value: role, label: role.replace(/_/g, ' ') }))
    },
    passwordMismatch() {
      if (!this.form.password || !this.form.password_confirmation) return false
      return this.form.password !== this.form.password_confirmation
    },
    passwordStrength() {
      const value = this.form.password || ''
      if (!value) return { key: 'empty', label: '', percent: 0 }
      let score = 0
      if (value.length >= 8) score++
      if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score++
      if (/\d/.test(value)) score++
      if (/[^A-Za-z0-9]/.test(value)) score++
      if (value.length >= 12) score++
      const map = [
        { key: 'weak', label: 'Weak', percent: 25 },
        { key: 'weak', label: 'Weak', percent: 35 },
        { key: 'fair', label: 'Fair', percent: 55 },
        { key: 'good', label: 'Good', percent: 75 },
        { key: 'strong', label: 'Strong', percent: 90 },
        { key: 'strong', label: 'Strong', percent: 100 }
      ]
      return map[Math.min(score, map.length - 1)]
    },
    photoInitials() {
      const name = (this.form.name || '').trim()
      if (!name) return '?'
      const parts = name.split(/\s+/)
      const first = parts[0] ? parts[0][0] : ''
      const second = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return (first + second).toUpperCase() || '?'
    },
    avatarGradient() {
      const palette = [
        'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)',
        'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        'linear-gradient(135deg, #6d28d9 0%, #a855f7 100%)',
        'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
        'linear-gradient(135deg, #be185d 0%, #ec4899 100%)'
      ]
      const seed = (this.form.name || this.form.email || '?')
      let hash = 0
      for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
      return palette[hash % palette.length]
    }
  },
  watch: {
    'form.organisation_id'(newId, oldId) {
      if (newId === oldId) return
      this.form.department_id = NO_DEPARTMENT
      this.departments = []
      if (newId) this.loadDepartments(newId)
      // State + Branch are GLOBAL — independent of org. Org switching does
      // NOT touch them.
    },
    'form.state_id'(newId, oldId) {
      if (newId === oldId) return
      if (!this.form.branch_id) return
      const branch = this.branches.find((b) => Number(b.id) === Number(this.form.branch_id))
      if (branch && Number(branch.state_id) !== Number(newId)) {
        this.form.branch_id = null
      }
    },
    'form.branch_id'(newId) {
      // Auto-fill state_id from the selected branch (branch carries state).
      if (!newId || !this.branches.length) return
      const branch = this.branches.find((b) => Number(b.id) === Number(newId))
      if (branch && Number(branch.state_id) !== Number(this.form.state_id)) {
        this.form.state_id = Number(branch.state_id)
      }
    }
  },
  created() {
    // hr_manager can only ever create tele sales staff — pre-select and lock it.
    if (this.isTelesalesOnlyCreator) {
      this.form.role = 'telesales'
    }
    this.loadOrganisations()
    this.loadStates()
    this.loadBranches()
  },
  methods: {
    loadOrganisations() {
      this.isLoadingOrganisations = true
      const token = localStorage.getItem('api_token')
      axios.get(`${this.$apiAdress}/api/organisations?token=${token}`)
        .then((response) => {
          const data = response.data
          if (Array.isArray(data)) this.organisations = data
          else if (Array.isArray(data?.data)) this.organisations = data.data
          else this.organisations = []
          if (!this.form.organisation_id && this.organisations.length) {
            this.form.organisation_id = this.organisations[0].id
          }
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingOrganisations = false })
    },
    loadDepartments(organisationId) {
      this.isLoadingDepartments = true
      const token = localStorage.getItem('api_token')
      axios.get(`${this.$apiAdress}/api/department?token=${token}&organisation_id=${organisationId}`)
        .then((response) => {
          const data = response.data
          if (Array.isArray(data)) this.departments = data
          else if (Array.isArray(data?.data)) this.departments = data.data
          else this.departments = []
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingDepartments = false })
    },
    loadStates() {
      this.isLoadingStates = true
      statesApi.list(this.$apiAdress)
        .then((data) => {
          this.states = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingStates = false })
    },
    loadBranches() {
      // Branches are GLOBAL — no org filter.
      this.isLoadingBranches = true
      branchesApi.list(this.$apiAdress)
        .then((data) => {
          this.branches = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingBranches = false })
    },
    goBack() {
      if (this.$router) {
        this.$router.push({ path: this.isTelesalesOnlyCreator ? '/staff/users' : '/users' })
        return
      }
      window.history.back()
    },
    triggerPhotoPicker() {
      this.$refs.photoInput && this.$refs.photoInput.click()
    },
    onPhotoSelected(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      if (!/^image\//.test(file.type)) {
        this.errorMessage = 'Profile photo must be an image file.'
        return
      }
      if (file.size > PHOTO_MAX_BYTES) {
        this.errorMessage = 'Profile photo must be 2 MB or smaller.'
        return
      }
      this.errorMessage = ''
      this.photoFile = file
      const reader = new FileReader()
      reader.onload = (e) => {
        this.photoPreview = e.target.result
      }
      reader.readAsDataURL(file)
    },
    clearPhoto() {
      this.photoFile = null
      this.photoPreview = ''
    },
    triggerCvPicker() {
      this.$refs.cvInput && this.$refs.cvInput.click()
    },
    onCvSelected(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      const isAllowed = file.type === 'application/pdf' || /^image\//.test(file.type)
      if (!isAllowed) {
        this.errorMessage = 'CV must be a PDF or an image file.'
        return
      }
      if (file.size > CV_MAX_BYTES) {
        this.errorMessage = 'CV must be 5 MB or smaller.'
        return
      }
      this.errorMessage = ''
      this.cvFile = file
      this.cvName = file.name
    },
    clearCv() {
      this.cvFile = null
      this.cvName = ''
    },
    resetForm() {
      const firstOrgId = this.organisations[0]?.id ?? null
      this.form = emptyForm()
      this.form.organisation_id = firstOrgId
      this.clearPhoto()
      this.clearCv()
      this.errorMessage = ''
      this.successMessage = ''
    },
    flattenErrorObject(errorPayload) {
      if (!errorPayload) return ''
      if (typeof errorPayload === 'string') return errorPayload
      if (typeof errorPayload === 'object') {
        return Object.values(errorPayload).flat().filter(Boolean).join(' ')
      }
      return ''
    },
    buildPayload() {
      return {
        name: this.form.name,
        email: this.form.email,
        password: this.form.password,
        password_confirmation: this.form.password_confirmation,
        organisation_id: this.form.organisation_id,
        role_name: this.form.role,
        department_id: this.form.department_id || null,
        department_name: this.selectedDepartmentName,
        branch_id: this.form.branch_id || null,
        state_id: this.form.state_id || null,
        phone: this.form.phone,
        alternate_phone: this.form.alternate_phone,
        address: this.form.address,
        city: this.form.city,
        state: this.form.state,
        pincode: this.form.pincode,
        country: this.form.country,
        date_of_birth: this.form.date_of_birth || null,
        gender: this.form.gender,
        blood_group: this.form.blood_group,
        marital_status: this.form.marital_status,
        emergency_contact_name: this.form.emergency_contact_name,
        emergency_contact_phone: this.form.emergency_contact_phone,
        emergency_contact_relation: this.form.emergency_contact_relation,
        employee_code: this.form.employee_code,
        biometric_id: this.form.biometric_id,
        designation: this.form.designation,
        date_of_joining: this.form.date_of_joining || null,
        employment_type: this.form.employment_type,
        employment_status: this.form.employment_status,
        pan_number: this.form.pan_number ? this.form.pan_number.toUpperCase() : '',
        aadhaar_number: this.form.aadhaar_number,
        bank_account_number: this.form.bank_account_number,
        bank_name: this.form.bank_name,
        bank_ifsc: this.form.bank_ifsc ? this.form.bank_ifsc.toUpperCase() : '',
        notes: this.form.notes
      }
    },
    register() {
      this.errorMessage = ''
      this.successMessage = ''

      if (!this.form.name || !this.form.email || !this.form.password || !this.form.password_confirmation) {
        this.errorMessage = 'Name, Email, Password and Repeat password are required.'
        return
      }
      if (this.passwordMismatch) {
        this.errorMessage = 'Passwords do not match.'
        return
      }
      if (!this.form.organisation_id) {
        this.errorMessage = 'Please select an organisation.'
        return
      }
      if (!this.form.role) {
        this.errorMessage = 'Please select a role.'
        return
      }

      this.isSubmitting = true

      const url = `${this.$apiAdress}/api/auth/register`
      const payload = this.buildPayload()
      let request

      if (this.photoFile || this.cvFile) {
        const fd = new FormData()
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null) return
          fd.append(key, value)
        })
        if (this.photoFile) fd.append('profile_photo', this.photoFile)
        if (this.cvFile) fd.append('cv_file', this.cvFile)
        request = axios.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      } else {
        request = axios.post(url, payload)
      }

      request
        .then((response) => {
          const message = response?.data?.message || 'User created successfully.'
          this.resetForm()
          this.successMessage = message
        })
        .catch((error) => {
          console.log(error)
          const apiError = error?.response?.data?.error
          this.errorMessage = this.flattenErrorObject(apiError) || error?.response?.data?.message || 'Unable to create user right now.'
        })
        .finally(() => {
          this.isSubmitting = false
        })
    }
  }
}
</script>

<style scoped>
.register-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.register-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.register-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.register-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.register-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.register-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.register-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.register-toolbar__actions>>>.btn.back-btn,
.register-form__actions>>>.btn.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.register-toolbar__actions>>>.btn.back-btn:hover:not(:disabled),
.register-form__actions>>>.btn.back-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.back-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.2s ease;
}

.register-toolbar__actions>>>.btn.back-btn:hover:not(:disabled) .back-btn__icon {
  transform: translateX(-2px);
}

/* ── Submit button ── */
.register-form__actions>>>.btn.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 1.1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.register-form__actions>>>.btn.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
  color: #ffffff;
}

.register-form__actions>>>.btn.submit-btn:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.register-form__actions>>>.btn.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  filter: saturate(0.6);
}

.submit-btn__icon {
  flex-shrink: 0;
  display: block;
}

/* ── Alert ── */
.register-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Section card ── */
.register-section {
  position: relative;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.register-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.register-section__heading {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.register-section__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #ffffff;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
  flex-shrink: 0;
}

.register-section__heading h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.register-section__heading p {
  margin: 0;
  font-size: 0.74rem;
  color: #6b7280;
}

.register-hint-pill {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  background: #f1f5f9;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  letter-spacing: 0.02em;
}

.register-section__body {
  padding: 1.1rem 1rem;
}

/* ── Profile row ── */
.profile-row {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 1.5rem;
  align-items: flex-start;
}

.profile-photo {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.6rem;
  padding: 0.75rem;
  border: 1px dashed #d8dde6;
  border-radius: 12px;
  background: #fbfcfe;
}

.profile-photo__avatar {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.18);
}

.profile-photo__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-photo__input {
  display: none;
}

.profile-photo__actions {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  justify-content: center;
}

.profile-photo__hint {
  margin: 0;
  font-size: 0.7rem;
  color: #6b7280;
}

.ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 30px;
  padding: 0 0.75rem;
  border: 1px solid #d4ece1;
  border-radius: 7px;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.ghost-btn:hover {
  background: #f5fbf8;
  border-color: #16a07a;
}

.ghost-btn--danger {
  color: #b91c1c;
  border-color: #fecaca;
}

.ghost-btn--danger:hover {
  background: #fef2f2;
  border-color: #ef4444;
}

.profile-fields {
  min-width: 0;
}

/* ── Form grids ── */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem 1rem;
}

.form-grid--single {
  grid-template-columns: 1fr;
}

.form-grid--triple {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.form-grid--quad {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem 1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-field--span2 {
  grid-column: span 2;
}

.form-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.form-input {
  width: 100%;
  height: 38px;
  padding: 0.3rem 0.7rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  appearance: none;
  -webkit-appearance: none;
}

.form-textarea {
  height: auto;
  min-height: 88px;
  padding: 0.55rem 0.7rem;
  resize: vertical;
  line-height: 1.5;
}

.form-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.form-input--error,
.form-input--error:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18);
}

.form-error {
  margin-top: 0.3rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #b91c1c;
}

/* ── Password ── */
.password-wrap {
  position: relative;
}

.password-wrap .form-input {
  padding-right: 4rem;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 0.4rem;
  transform: translateY(-50%);
  height: 28px;
  padding: 0 0.6rem;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #0e5b46;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.password-toggle:hover {
  background: #f5fbf8;
  border-color: #d4ece1;
}

.password-strength {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.45rem;
}

.password-strength__bar {
  flex: 1;
  height: 5px;
  background: #eef1f7;
  border-radius: 999px;
  overflow: hidden;
}

.password-strength__bar>span {
  display: block;
  height: 100%;
  border-radius: 999px;
  transition: width 0.2s ease, background 0.2s ease;
}

.password-strength__label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.password-strength--weak .password-strength__bar>span {
  background: #ef4444;
}

.password-strength--weak .password-strength__label {
  color: #b91c1c;
}

.password-strength--fair .password-strength__bar>span {
  background: #f59e0b;
}

.password-strength--fair .password-strength__label {
  color: #b45309;
}

.password-strength--good .password-strength__bar>span {
  background: #0ea5e9;
}

.password-strength--good .password-strength__label {
  color: #0369a1;
}

.password-strength--strong .password-strength__bar>span {
  background: #16a07a;
}

.password-strength--strong .password-strength__label {
  color: #0e5b46;
}

/* ── Footer ── */
.register-form__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.85rem 1rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.form-meta {
  font-size: 0.78rem;
  color: #6b7280;
}

.register-form__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* ── CV / document upload ── */
.cv-upload__input {
  display: none;
}

.cv-upload {
  border: 1px dashed #d8dde6;
  border-radius: 12px;
  background: #fbfcfe;
  padding: 0.9rem 1rem;
}

.cv-upload__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.cv-upload__info {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
  color: #4f5560;
}

.cv-upload__info svg {
  flex-shrink: 0;
  color: #16a07a;
}

.cv-upload__name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2a44;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cv-upload__empty {
  font-size: 0.85rem;
  color: #6b7280;
}

.cv-upload__link {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0e5b46;
  text-decoration: underline;
}

.cv-upload__actions {
  display: flex;
  gap: 0.4rem;
  flex-shrink: 0;
}

.cv-upload__hint {
  margin: 0.6rem 0 0;
  font-size: 0.7rem;
  color: #6b7280;
}

/* ── Responsive ── */
@media (max-width: 767px) {
  .profile-row {
    grid-template-columns: 1fr;
  }

  .form-grid--triple {
    grid-template-columns: 1fr 1fr;
  }

  .form-grid--quad {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 575px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-grid--triple {
    grid-template-columns: 1fr;
  }

  .form-grid--quad {
    grid-template-columns: 1fr;
  }

  .form-field--span2 {
    grid-column: span 1;
  }

  .register-form__footer {
    flex-direction: column;
    align-items: stretch;
  }

  .register-form__actions {
    justify-content: flex-end;
  }
}
</style>
